import type {
  DashboardApiResponse,
  ViewsTriggersApiResponse,
  ContactsApiResponse,
  MediaUploadRequest,
  MediaUploadResponse,
  TriggerData,
  CreateBoxRequest,
  CreatedBox,
  UserBoxesApiResponse,
  BoxAnswer,
  StatusInfo,
  UserStatusSummary,
} from "../types/dashboard";
import { store } from "../store";
import { clearData, setStatusIdFromUrl } from "../store/dashboardSlice";

const API_BASE_URL = import.meta.env.DEV
  ? "/api"
  : "https://twistatus-statistics.twist.bot/api";
const ASKME_API_BASE_URL = import.meta.env.DEV
  ? "/askme-api"
  : "https://askme.twist.bot/api";
const TOKEN_STORAGE_KEY = "twistatus_token";

interface ApiRequestConfig {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: string;
  credentials?: RequestCredentials;
}

interface ApiError extends Error {
  status?: number;
  isCorsError?: boolean;
}

const LOGIN_SOURCE_KEY = "twistatus_login_source";
const PASSKEY_PROMPT_DISMISSED_KEY = "twistatus_passkey_prompt_dismissed";

export const extractAndSaveToken = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    return token;
  }

  return null;
};

export const saveTokenFromLogin = (token: string): void => {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(LOGIN_SOURCE_KEY, "login_page");
};

export const markBotLoginSource = (): void => {
  localStorage.setItem(LOGIN_SOURCE_KEY, "bot");
};

export const isBotLoginSource = (): boolean => {
  return localStorage.getItem(LOGIN_SOURCE_KEY) === "bot";
};

export const markPasskeyLoginSource = (): void => {
  localStorage.setItem(LOGIN_SOURCE_KEY, "passkey");
};

export const isPasskeyLoginSource = (): boolean => {
  return localStorage.getItem(LOGIN_SOURCE_KEY) === "passkey";
};

export const isPasskeyPromptDismissed = (): boolean => {
  return localStorage.getItem(PASSKEY_PROMPT_DISMISSED_KEY) === "true";
};

export const dismissPasskeyPrompt = (): void => {
  localStorage.setItem(PASSKEY_PROMPT_DISMISSED_KEY, "true");
};

export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

// Logout: clear all auth-related data from localStorage
export const logout = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(LOGIN_SOURCE_KEY);
  localStorage.removeItem(PASSKEY_PROMPT_DISMISSED_KEY);
  store.dispatch(clearData());
};

// Extract status_id from URL and save to Redux
export const extractAndSaveStatusId = (): number | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const statusIdParam = urlParams.get("status_id");

  if (statusIdParam) {
    const statusId = parseInt(statusIdParam, 10);
    if (!isNaN(statusId)) {
      store.dispatch(setStatusIdFromUrl(statusId));

      urlParams.delete("status_id");
      urlParams.delete("token");

      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);

      return statusId;
    }
  }

  return null;
};

const getAuthToken = (): string => {
  const token = extractAndSaveToken() || getStoredToken();
  if (!token) {
    throw new Error(
      "No authentication token found. Please provide token in URL parameter or ensure it's saved in localStorage."
    );
  }
  return token;
};

const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getStatusIdentifier = (): number | "me" => {
  const { statusIdFromUrl } = store.getState().dashboard;
  return statusIdFromUrl ?? "me";
};

const createHeaders = (
  token: string,
  additionalHeaders: Record<string, string> = {}
): Record<string, string> => {
  return {
    accept: "*/*",
    authorization: `Bearer ${token}`,
    "X-Requested-With": "XMLHttpRequest",
    "X-Timezone": getUserTimezone(),
    ...additionalHeaders,
  };
};

const handleApiError = (error: unknown, context: string): never => {
  console.error(`Error in ${context}:`, error);

  if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
    const corsError = new Error(`CORS Error: ${error.message}`) as ApiError;
    corsError.isCorsError = true;
    throw corsError;
  }

  throw error;
};

const handleHttpError = async (
  response: Response,
  context: string
): Promise<never> => {
  const errorText = await response.text();
  console.error(`${context} Error Response:`, errorText);

  if (response.status === 401 || response.status === 403) {
    const authError = new Error(
      `Authentication failed. Please check your token. Status: ${response.status}`
    ) as ApiError;
    authError.status = response.status;
    throw authError;
  }

  const httpError = new Error(
    `HTTP error! status: ${response.status} - ${errorText}`
  ) as ApiError;
  httpError.status = response.status;
  throw httpError;
};

const makeApiRequest = async <T>(
  url: string,
  config: ApiRequestConfig = {},
  context: string
): Promise<T> => {
  const token = getAuthToken();
  const headers = createHeaders(token, config.headers);

  const requestConfig: RequestInit = {
    method: config.method || "GET",
    headers,
    credentials: config.credentials || "omit",
    ...(config.body && { body: config.body }),
  };

  try {
    const response = await fetch(url, requestConfig);

    if (!response.ok) {
      await handleHttpError(response, context);
    }

    return await response.json();
  } catch (error) {
    return handleApiError(error, context);
  }
};

export const createAnonymousBox = async (
  payload: CreateBoxRequest
): Promise<CreatedBox> => {
  const response = await fetch(`${ASKME_API_BASE_URL}/boxes/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Create box failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as CreatedBox;
};

export const fetchUserBoxes = async (
  userId: string | number
): Promise<UserBoxesApiResponse> => {
  const response = await fetch(
    `${ASKME_API_BASE_URL}/users/${userId}/boxes`,
    {
      headers: { accept: "application/json" },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Fetch user boxes failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as UserBoxesApiResponse;
};

export const fetchBoxByShortCode = async (
  shortCode: string
): Promise<CreatedBox> => {
  const response = await fetch(
    `${ASKME_API_BASE_URL}/boxes/code/${shortCode}`,
    {
      headers: { accept: "application/json" },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Fetch box by short code failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as CreatedBox;
};

export const fetchBoxByPublicId = async (
  identifier: string
): Promise<{ id: string; title: string; short_code: string; is_active?: number }> => {
  const response = await fetch(
    `${ASKME_API_BASE_URL}/boxes/public/${identifier}`,
    {
      headers: { accept: "application/json" },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Fetch box by public id failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as { id: string; title: string; short_code: string; is_active?: number };
};

export const fetchQuestionsByBoxId = async (
  boxId: string
): Promise<BoxAnswer[]> => {
  const response = await fetch(
    `${ASKME_API_BASE_URL}/questions/box/${boxId}`,
    {
      headers: { accept: "application/json" },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Fetch questions by box failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as BoxAnswer[];
};

export const updateQuestionStatus = async (
  questionId: string,
  status: string
): Promise<BoxAnswer> => {
  const response = await fetch(
    `${ASKME_API_BASE_URL}/questions/${questionId}/status`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ status }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Update question status failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as BoxAnswer;
};

export const createAnonymousQuestion = async (
  boxId: string,
  questionText: string,
  source = "web"
): Promise<{ id: string }> => {
  const response = await fetch(`${ASKME_API_BASE_URL}/questions/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify({ boxId, questionText, source }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Create question failed with status ${response.status}: ${errorText}`
    );
  }

  return (await response.json()) as { id: string };
};

export const deleteBox = async (id: string): Promise<void> => {
  const response = await fetch(`${ASKME_API_BASE_URL}/boxes/${id}`, {
    method: "DELETE",
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Delete box failed with status ${response.status}: ${errorText}`
    );
  }
};

export const linkBoxToUser = async (
  adminId: string,
  userId: string
): Promise<void> => {
  const response = await fetch(`${ASKME_API_BASE_URL}/boxes/link-to-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify({ adminId, userId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Link box to user failed with status ${response.status}: ${errorText}`
    );
  }
};

export const fetchDashboardData = async (): Promise<DashboardApiResponse> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/dashboard?period=total`;
  return makeApiRequest<DashboardApiResponse>(url, {}, "Dashboard API");
};

export const fetchDashboardDataWithPeriod = async (
  period: string
): Promise<DashboardApiResponse> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/dashboard?period=${period}`;
  return makeApiRequest<DashboardApiResponse>(
    url,
    {},
    `Dashboard API (period=${period})`
  );
};

export const fetchViewsTriggersData =
  async (): Promise<ViewsTriggersApiResponse> => {
    const statusIdentifier = getStatusIdentifier();
    const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/triggers?last=this%20month&group_by=day`;
    return makeApiRequest<ViewsTriggersApiResponse>(
      url,
      {},
      "Views/Triggers API"
    );
  };

export const fetchYearlyViewsTriggersData =
  async (): Promise<ViewsTriggersApiResponse> => {
    const statusIdentifier = getStatusIdentifier();
    const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/triggers?last=this%20year&group_by=month`;
    return makeApiRequest<ViewsTriggersApiResponse>(
      url,
      {},
      "Yearly Views/Triggers API"
    );
  };

export const fetchWeeklyViewsTriggersData =
  async (): Promise<ViewsTriggersApiResponse> => {
    const statusIdentifier = getStatusIdentifier();
    const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/triggers?last=this%20week&group_by=day`;
    return makeApiRequest<ViewsTriggersApiResponse>(
      url,
      {},
      "Weekly Views/Triggers API"
    );
  };

export const fetchDailyViewsTriggersData =
  async (): Promise<ViewsTriggersApiResponse> => {
    const statusIdentifier = getStatusIdentifier();
    const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/triggers?last=today&group_by=hour`;
    return makeApiRequest<ViewsTriggersApiResponse>(
      url,
      {},
      "Daily Views/Triggers API"
    );
  };

export const fetchForeverViewsTriggersData =
  async (): Promise<ViewsTriggersApiResponse> => {
    const statusIdentifier = getStatusIdentifier();
    const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/triggers?last=forever&group_by=month`;
    return makeApiRequest<ViewsTriggersApiResponse>(
      url,
      {},
      "Forever Views/Triggers API"
    );
  };

export const fetchViewsSourcesData = async (): Promise<{ data?: unknown; [key: string]: unknown }> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/reports/views/sources?last=this%20year&group_by=month`;
  const data = await makeApiRequest<{ data?: unknown; [key: string]: unknown }>(url, {}, "Views Sources API");
  console.log("Views Sources Data:", data);
  return data;
};

export const fetchRatingsData = async (
  from?: string,
  to?: string
): Promise<{ success?: boolean; data?: unknown; [key: string]: unknown }> => {
  const statusIdentifier = getStatusIdentifier();
  let url = `${API_BASE_URL}/status/${statusIdentifier}/reports/ratings`;

  const params = new URLSearchParams();

  if (from) {
    params.append("start", from);
  }

  if (to) {
    params.append("end", to);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log(
    "fetchRatingsData called:",
    "start:",
    from,
    "end:",
    to,
    "URL:",
    url
  );

  const data = await makeApiRequest<{ success?: boolean; data?: unknown; [key: string]: unknown }>(url, {}, "Ratings API");
  console.log("Ratings API response:", data);
  return data;
};

export const fetchStatusMe = async (): Promise<{ data?: { status?: StatusInfo }; [key: string]: unknown }> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}`;
  return makeApiRequest<{ data?: { status?: StatusInfo }; [key: string]: unknown }>(url, {}, "Status Me API");
};

export const fetchTriggersData = async (): Promise<TriggerData[]> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/triggers`;
  return makeApiRequest<TriggerData[]>(url, {}, "Triggers API");
};

export const fetchAllTriggersData = async (): Promise<TriggerData[] | { success: boolean; data: TriggerData[] }> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/triggers?all=true`;
  return makeApiRequest<TriggerData[] | { success: boolean; data: TriggerData[] }>(url, {}, "All Triggers API");
};

export const fetchContactsData = async (
  fromDate?: string,
  toDate?: string,
  limit?: number,
  offset?: number,
  source?: string,
  query?: string,
  triggerId?: number
): Promise<ContactsApiResponse> => {
  const statusIdentifier = getStatusIdentifier();
  let url = `${API_BASE_URL}/status/${statusIdentifier}/contacts`;

  const params = new URLSearchParams();

  if (fromDate === "today") {
    params.append("last", "today");
  } else if (fromDate === "this_week") {
    params.append("last", "this week");
  } else if (fromDate) {
    params.append("start", fromDate);
  }

  if (toDate) {
    params.append("end", toDate);
  }

  if (limit !== undefined) {
    params.append("limit", limit.toString());
  }
  if (offset !== undefined) {
    params.append("offset", offset.toString());
  }

  if (source) {
    params.append("source", source);
  }

  if (query && query.trim()) {
    let processedQuery = query.trim();

    if (processedQuery.startsWith("0") && /^0\d+$/.test(processedQuery)) {
      processedQuery = processedQuery.substring(1);
    }

    params.append("query", processedQuery);
  }

  if (triggerId !== undefined) {
    params.append("trigger_id", triggerId.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log(
    "fetchContactsData called:",
    "from:",
    fromDate,
    "to:",
    toDate,
    "limit:",
    limit,
    "offset:",
    offset,
    "source:",
    source,
    "query:",
    query,
    "triggerId:",
    triggerId,
    "URL:",
    url,
    "Params:",
    params.toString()
  );

  const data = await makeApiRequest<ContactsApiResponse>(
    url,
    {},
    "Contacts API"
  );
  console.log("fetchContactsData response:", data);
  return data;
};

export const updateContactField = async (
  statusId: number,
  field: string,
  value: string | string[] | { name: string }[] | { Name: string }[]
): Promise<unknown> => {
  const statusOverride = store.getState().dashboard.statusIdFromUrl;
  const targetStatusId = statusOverride ?? statusId;
  const url = `${API_BASE_URL}/status/${targetStatusId}`;
  const body = {
    [field]: value,
  };

  return makeApiRequest<unknown>(
    url,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Update Contact API"
  );
};

export const createShortLink = async (
  triggerId: number
): Promise<{ short_link_id: string }> => {
  const url = `${API_BASE_URL}/trigger/${triggerId}/shortlink`;
  return makeApiRequest<{ short_link_id: string }>(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    "Create Short Link API"
  );
};

export const initiateOAuthConnection = async (
  statusId: number
): Promise<{ success: boolean; message: string; data?: { id: string } }> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/connection`;
  const data = await makeApiRequest<{
    success: boolean;
    message: string;
    data?: { id: string };
  }>(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: statusId }),
    },
    "OAuth Connection API"
  );
  console.log("OAuth Connection API Response:", data);
  return data;
};

export const fetchCategories = async (): Promise<
  { id: number; name: string }[]
> => {
  const url = `${API_BASE_URL}/category`;
  const responseData = await makeApiRequest<{ success: boolean; data: { Name: string }[] }>(url, {}, "Categories API");
  console.log("Categories API response:", responseData);
  
  if (responseData.success && Array.isArray(responseData.data)) {
    return responseData.data.map((item, index) => ({
      id: index + 1,
      name: item.Name,
    }));
  }

  return [];
};

export const fetchStatusConnections = async (): Promise<{
  success: boolean;
  message: string;
  data: Array<{
    ID: number;
    UpdatedAt: string;
    DeletedAt: string | null;
    id: string;
    state: string;
    email: string;
    status_id: number;
    CreatedAt: string;
  }>;
}> => {
  const statusIdentifier = getStatusIdentifier();
  const url = `${API_BASE_URL}/status/${statusIdentifier}/connections`;
  return makeApiRequest(url, {}, "Status Connections API");
};

export const uploadMedia = async (
  base64Data: string,
  type: string,
  url: string
): Promise<MediaUploadResponse> => {
  const statusIdentifier = getStatusIdentifier();
  const apiUrl = `${API_BASE_URL}/status/${statusIdentifier}/media`;
  const body: MediaUploadRequest = {
    base64_data: base64Data,
    type: type,
    ...(url && { url: url }),
  };

  console.log("Upload Media Request Body:", {
    base64_data: base64Data.substring(0, 100) + "...",
    type: type,
    url: url || "",
  });

  return makeApiRequest<MediaUploadResponse>(
    apiUrl,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Upload Media API"
  );
};

export const exportContactsAsCSV = async (
  fromDate?: string,
  toDate?: string,
  source?: string,
  query?: string,
  triggerId?: number
): Promise<void> => {
  const statusIdentifier = getStatusIdentifier();
  let url = `${API_BASE_URL}/status/${statusIdentifier}/contacts/export/csv`;

  const params = new URLSearchParams();

  if (fromDate) {
    if (fromDate === "today" || fromDate === "this_week") {
      params.append("last", fromDate);
    } else {
      params.append("from", fromDate);
    }
  }

  if (toDate && fromDate !== "today" && fromDate !== "this_week") {
    params.append("end", toDate);
  }

  if (source) {
    params.append("source", source);
  }

  if (query && query.trim()) {
    let processedQuery = query.trim();

    if (processedQuery.startsWith("0") && /^0\d+$/.test(processedQuery)) {
      processedQuery = processedQuery.substring(1);
    }

    params.append("query", processedQuery);
  }

  if (triggerId !== undefined) {
    params.append("trigger_id", triggerId.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log("exportContactsAsCSV called with:", {
    fromDate,
    toDate,
    source,
    query,
    triggerId,
    url,
  });

  const token = getStoredToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const headers = {
    accept: "*/*",
    authorization: `Bearer ${token}`,
    "X-Requested-With": "XMLHttpRequest",
  };

  try {
    const response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "omit",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Export CSV API Error Response:", errorText);

      if (response.status === 401 || response.status === 403) {
        throw new Error(
          `Authentication failed. Please check your token. Status: ${response.status}`
        );
      }

      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    let blob = await response.blob();

    const text = await blob.text();
    const bom = "\uFEFF";
    blob = new Blob([bom + text], { type: "text/csv;charset=utf-8" });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const today = new Date();
    const dateStr = today.toISOString().split("T")[0];
    const filename = `אנשי-קשר-${dateStr}.csv`;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);

    console.log("CSV file downloaded successfully");
  } catch (error) {
    console.error("Error exporting contacts as CSV:", error);

    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(`CORS Error: ${error.message}`);
    }

    throw error;
  }
};

export const fetchTriggerDetails = async (
  triggerId: number
): Promise<TriggerData> => {
  const url = `${API_BASE_URL}/trigger/${triggerId}`;
  return makeApiRequest<TriggerData>(url, {}, "Trigger Details API");
};

export const exportContactsAsVCF = async (
  fromDate?: string,
  toDate?: string,
  source?: string,
  query?: string,
  triggerId?: number
): Promise<void> => {
  const statusIdentifier = getStatusIdentifier();
  let url = `${API_BASE_URL}/status/${statusIdentifier}/contacts/export/vcf`;

  const params = new URLSearchParams();

  if (fromDate) {
    if (fromDate === "today" || fromDate === "this_week") {
      params.append("last", fromDate);
    } else {
      params.append("from", fromDate);
    }
  }

  if (toDate && fromDate !== "today" && fromDate !== "this_week") {
    params.append("end", toDate);
  }

  if (source) {
    params.append("source", source);
  }

  if (query && query.trim()) {
    let processedQuery = query.trim();

    if (processedQuery.startsWith("0") && /^0\d+$/.test(processedQuery)) {
      processedQuery = processedQuery.substring(1);
    }

    params.append("query", processedQuery);
  }

  if (triggerId !== undefined) {
    params.append("trigger_id", triggerId.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log("exportContactsAsVCF called with:", {
    fromDate,
    toDate,
    source,
    query,
    triggerId,
    url,
  });

  const token = getStoredToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const headers = {
    accept: "*/*",
    authorization: `Bearer ${token}`,
    "X-Requested-With": "XMLHttpRequest",
  };

  try {
    const response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "omit",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Export VCF API Error Response:", errorText);

      if (response.status === 401 || response.status === 403) {
        throw new Error(
          `Authentication failed. Please check your token. Status: ${response.status}`
        );
      }

      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    let blob = await response.blob();

    const text = await blob.text();
    const bom = "\uFEFF";
    blob = new Blob([bom + text], { type: "text/vcard;charset=utf-8" });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const today = new Date();
    const dateStr = today.toISOString().split("T")[0];
    const filename = `אנשי-קשר-${dateStr}.vcf`;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);

    console.log("VCF file downloaded successfully");
  } catch (error) {
    console.error("Error exporting contacts as VCF:", error);

    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(`CORS Error: ${error.message}`);
    }

    throw error;
  }
};

// Fetch user's statuses/accounts list
export const fetchUserStatuses = async (): Promise<UserStatusSummary[]> => {
  const url = `${API_BASE_URL}/user/me/statuses`;
  const response = await makeApiRequest<{ data: UserStatusSummary[] }>(url, {}, "User Statuses API");
  return response.data;
};


const OAUTH_STATE_KEY = "twistatus_oauth_state";

export const getGoogleOAuthURL = async (
  redirectUri?: string
): Promise<{
  url: string;
  state: string;
}> => {
  const url = redirectUri
    ? `${API_BASE_URL}/auth/google-login/url?redirect_uri=${encodeURIComponent(redirectUri)}`
    : `${API_BASE_URL}/auth/google-login/url`;
  const response = await fetch(url);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to get Google login URL: ${response.status} - ${errorText}`
    );
  }

  return response.json();
};

export const storeOAuthState = (state: string): void => {
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
};

export const consumeOAuthState = (): string | null => {
  const state = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  return state;
};

export class GoogleLoginError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export const googleLoginCallback = async (
  code: string,
  state: string,
  redirectUri?: string
): Promise<{ token: string; expire: string }> => {
  const body: Record<string, string> = { code, state };
  if (redirectUri) body.redirect_uri = redirectUri;

  const response = await fetch(`${API_BASE_URL}/auth/google-login/callback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorCode = "unknown";
    let errorMessage = `Google login failed (${response.status})`;

    try {
      const errorBody = await response.json();
      if (errorBody.error) errorCode = errorBody.error;
      if (errorBody.message) errorMessage = errorBody.message;
    } catch {
      // Response wasn't JSON, use defaults
    }

    throw new GoogleLoginError(response.status, errorCode, errorMessage);
  }

  return response.json();
};

export const passkeyLoginBegin = async (): Promise<{ publicKey?: Record<string, unknown>; [key: string]: unknown }> => {
  const url = `${API_BASE_URL}/auth/passkey/login/begin`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Passkey login begin failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const passkeyLoginFinish = async (
  credential: object
): Promise<{ token: string; expire: string }> => {
  const url = `${API_BASE_URL}/auth/passkey/login/finish`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(credential),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Passkey login finish failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const passkeyRegisterBegin = async (): Promise<{ publicKey?: Record<string, unknown>; [key: string]: unknown }> => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}/auth/passkey/register/begin`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Passkey register begin failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const passkeyRegisterFinish = async (
  credential: object
): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}/auth/passkey/register/finish`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify(credential),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Passkey register finish failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};
